// ═══════════════════════════════════════════════════════════════════════════════════════════════════
// ReasoningEffortCard — Agent Studio UI for managing per-agent reasoning effort
// ═══════════════════════════════════════════════════════════════════════════════════════════════════
//
// Lives on the agent detail page's Overview tab. Three goals:
//
//   1. AUTHORS UNDERSTAND WHAT THEY'RE PICKING — every level shows its
//      description ("when to use this") inline. Not hidden in a tooltip,
//      not revealed on hover. Visible the entire time you're choosing.
//
//   2. PROVENANCE IS ALWAYS HONEST — when the agent's preferred level
//      diverges from what would actually run (e.g. preferred=High but the
//      bound model has SupportsReasoningEffort=false), the UI shows BOTH
//      values and explains the divergence. No silent lies.
//
//   3. CHANGES ARE AUDITED — clicking a different level prompts for a
//      reason (especially for downgrades). The audit trail is one click
//      away ("Recent changes" → drawer with full history).
//
// Backed by IReasoningEffortManager via /api/AgentReasoning/* endpoints.
// ═══════════════════════════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  Modal,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  DollarOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  ThunderboltFilled,
  WarningFilled,
} from '@ant-design/icons';
import {
  getAgentReasoning,
  getReasoningHistory,
  getReasoningLevels,
  setAgentReasoning,
  type AgentReasoningState,
  type EffortSource,
  type ReasoningHistoryRow,
  type ReasoningLevel,
} from '@/api/agentReasoning';
import { SectionCard } from '@/components/SectionCard';
import { colors } from '@/theme/tokens';

const { Text, Paragraph } = Typography;

// ────────────────────────────────────────────────────────────────────────────
// Visual hints per level — color, cost-tier, latency-tier. These ride along
// with the DB-supplied Description so the card carries both authoring
// guidance ("when to pick this") and economic guidance ("what it costs").
// ────────────────────────────────────────────────────────────────────────────

interface LevelStyle {
  /** Border + accent color of the card. */
  accent: string;
  /** Soft fill behind the card when selected. */
  selectedFill: string;
  /** Single-character cost tier (visible glance: Low=1, Med=2, High=3). */
  costTier: string;       // "$" / "$$" / "$$$"
  /** Latency hint shown alongside cost. */
  latency: string;        // "Fast" / "Balanced" / "Deep"
  /** Short tagline shown above the description. */
  tagline: string;
}

const LEVEL_STYLES: Record<string, LevelStyle> = {
  Low: {
    accent: colors.green[500],
    // colors.green only has 100/500/600/700 — no 50. Using 100 as the
    // softest available shade for the selected-state fill.
    selectedFill: colors.green[100],
    costTier: '$',
    latency: 'Fast',
    tagline: 'Quick & cheap',
  },
  Medium: {
    accent: colors.ink[500],
    selectedFill: colors.ink[50],
    costTier: '$$',
    latency: 'Balanced',
    tagline: 'Default — balanced effort',
  },
  High: {
    accent: colors.violet[600],
    selectedFill: colors.violet[50],
    costTier: '$$$',
    latency: 'Deep',
    tagline: 'Most thorough — for safety-critical decisions',
  },
};

const FALLBACK_STYLE: LevelStyle = {
  accent: colors.slate[500],
  selectedFill: colors.slate[100],
  costTier: '?',
  latency: '?',
  tagline: '',
};

// ────────────────────────────────────────────────────────────────────────────
// Source → human-friendly label for the resolved-from chip
// ────────────────────────────────────────────────────────────────────────────

const SOURCE_LABEL: Record<EffortSource, string> = {
  ModelDoesNotSupport: 'Model can\'t accept reasoning_effort',
  InheritedFromCaller: 'Inherited from upstream agent (A2A)',
  AgentOverride: 'This agent\'s configured level',
  ModelDefault: 'Model default (legacy)',
  ApiAutoDetectMedium: 'API auto-detected as reasoning model',
  NoneConfigured: 'No level configured',
};

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

interface Props {
  agentId: number;
  /** Optional dark-mode toggle so colors invert correctly. */
  isDark?: boolean;
}

export function ReasoningEffortCard({ agentId, isDark = false }: Props) {
  const [levels, setLevels] = useState<ReasoningLevel[]>([]);
  const [state, setState] = useState<AgentReasoningState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // ── Initial load ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [lvls, st] = await Promise.all([
          getReasoningLevels(),
          getAgentReasoning(agentId),
        ]);
        if (cancelled) return;
        setLevels(lvls.sort((a, b) => a.displayOrder - b.displayOrder));
        setState(st);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  // ── Determine if proposed level is a "downgrade" — prompts for reason ──
  const isDowngrade = (currentId: number, proposedId: number): boolean => {
    const cur = levels.find((l) => l.id === currentId);
    const prop = levels.find((l) => l.id === proposedId);
    if (!cur || !prop) return false;
    return prop.displayOrder < cur.displayOrder;
  };

  // ── Save handler — prompts for reason if downgrading ────────────────
  const handleSelect = async (newLevelId: number) => {
    if (!state) return;
    if (newLevelId === state.preferredLevelId) return; // no-op

    let reason: string | null = null;

    if (isDowngrade(state.preferredLevelId, newLevelId)) {
      // Downgrades get a confirmation modal with a reason field. This is
      // the regulatory-defense breadcrumb: every downgrade has a why.
      const downgradeReason = await promptForReason(
        levels.find((l) => l.id === state.preferredLevelId)?.name ?? '?',
        levels.find((l) => l.id === newLevelId)?.name ?? '?',
      );
      if (downgradeReason === null) return; // cancelled
      reason = downgradeReason;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await setAgentReasoning(agentId, {
        newLevelId,
        reason: reason ?? undefined,
      });
      setState(updated);
      void message.success({
        content: (
          <span>
            Reasoning effort updated to <strong>{updated.preferredLevelName}</strong>
            {!updated.shouldInject && (
              <>
                {' '}— but the bound model can't accept it, so it'll be ignored at runtime.
              </>
            )}
          </span>
        ),
        duration: 5,
      });
    } catch (e) {
      void message.error('Save failed: ' + (e as Error).message);
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SectionCard title="Reasoning effort" subtitle="How hard the agent thinks before answering">
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      </SectionCard>
    );
  }

  if (error || !state) {
    return (
      <SectionCard title="Reasoning effort" subtitle="How hard the agent thinks before answering">
        <Alert type="error" showIcon message={error ?? 'Failed to load.'} />
      </SectionCard>
    );
  }

  const divergence = state.shouldInject
    ? null
    : state.modelSupportsReasoning
    ? null
    : `This agent is currently bound to model ${state.modelName} which doesn't accept the reasoning_effort parameter. Your selection here is saved on the agent, but it's silently ignored at runtime — chat works normally without it.`;

  return (
    <>
      <SectionCard
        title={
          <span>
            Reasoning effort{' '}
            <Tooltip
              title={
                <span>
                  Reasoning models (like o1, o3-mini, gpt-5-reasoning) can do extra
                  thinking before answering. This level controls how hard. Higher = more
                  careful and accurate, but slower and more expensive. Lower = quick and
                  cheap. Pick based on the agent's safety stakes.
                </span>
              }
            >
              <InfoCircleOutlined style={{ color: colors.slate[400], fontSize: 14 }} />
            </Tooltip>
          </span>
        }
        subtitle="How hard the agent thinks before answering"
        actions={
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => setHistoryOpen(true)}
          >
            Recent changes
          </Button>
        }
      >
        {/* ── Capability warning ──────────────────────────────────── */}
        {divergence && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningFilled />}
            message="Selection will be silently ignored at runtime"
            description={divergence}
            style={{ marginBottom: 14 }}
          />
        )}

        {/* ── The three picker cards ──────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 12,
            marginBottom: 14,
          }}
        >
          {levels.map((l) => {
            const sty = LEVEL_STYLES[l.name] ?? FALLBACK_STYLE;
            const isSelected = l.id === state.preferredLevelId;
            return (
              <LevelCard
                key={l.id}
                level={l}
                style={sty}
                isSelected={isSelected}
                isSaving={saving}
                isDark={isDark}
                onSelect={() => void handleSelect(l.id)}
              />
            );
          })}
        </div>

        {/* ── Live "what would fire right now" indicator ──────────── */}
        <ResolvedSummary state={state} levels={levels} isDark={isDark} />
      </SectionCard>

      <HistoryDrawer agentId={agentId} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Level card — one of three in the picker grid
// ────────────────────────────────────────────────────────────────────────────

function LevelCard({
  level,
  style: sty,
  isSelected,
  isSaving,
  isDark,
  onSelect,
}: {
  level: ReasoningLevel;
  style: LevelStyle;
  isSelected: boolean;
  isSaving: boolean;
  isDark: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isSaving}
      style={{
        all: 'unset',
        cursor: isSaving ? 'wait' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 14,
        borderRadius: 10,
        border: `2px solid ${isSelected ? sty.accent : isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isSelected
          ? sty.selectedFill
          : isDark
          ? colors.slate[800]
          : '#FFFFFF',
        transition: 'all 0.15s ease',
        position: 'relative',
        boxShadow: isSelected ? `0 2px 12px ${sty.accent}33` : 'none',
      }}
      onMouseEnter={(e) => {
        if (!isSelected && !isSaving) {
          e.currentTarget.style.borderColor = sty.accent;
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = isDark ? colors.slate[700] : colors.slate[200];
        }
      }}
    >
      {/* Selected check badge */}
      {isSelected && (
        <CheckCircleFilled
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: sty.accent,
            fontSize: 18,
          }}
        />
      )}

      {/* Header row — name + cost tier + latency */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Text
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: isSelected ? sty.accent : isDark ? colors.slate[100] : colors.slate[900],
          }}
        >
          {level.name}
        </Text>
        <Tooltip title={`Approximate cost: ${sty.costTier}`}>
          <Tag
            icon={<DollarOutlined />}
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              padding: '0 6px',
              margin: 0,
              borderColor: sty.accent + '66',
              color: sty.accent,
              background: 'transparent',
            }}
          >
            {sty.costTier}
          </Tag>
        </Tooltip>
        <Tooltip title={`Latency: ${sty.latency}`}>
          <Tag
            icon={<ClockCircleOutlined />}
            style={{
              fontSize: 11,
              padding: '0 6px',
              margin: 0,
              borderColor: sty.accent + '66',
              color: sty.accent,
              background: 'transparent',
            }}
          >
            {sty.latency}
          </Tag>
        </Tooltip>
      </div>

      {/* Tagline — short hook for the card */}
      <Text
        style={{
          fontSize: 12.5,
          fontStyle: 'italic',
          color: isDark ? colors.slate[300] : colors.slate[600],
        }}
      >
        {sty.tagline}
      </Text>

      {/* Description — DB-supplied authoring guidance, always visible */}
      <Paragraph
        style={{
          margin: 0,
          fontSize: 12.5,
          lineHeight: 1.55,
          color: isDark ? colors.slate[200] : colors.slate[700],
        }}
      >
        {level.description}
      </Paragraph>

      {/* Wire value (mono, subtle) — what actually goes on the wire */}
      <Text
        style={{
          fontSize: 10.5,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[500] : colors.slate[400],
          marginTop: 'auto',
        }}
      >
        wire: <code>{level.wireValue}</code>
      </Text>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Resolved summary — small inline card showing "what would actually fire"
// ────────────────────────────────────────────────────────────────────────────

function ResolvedSummary({
  state,
  levels: _levels,
  isDark,
}: {
  state: AgentReasoningState;
  levels: ReasoningLevel[];
  isDark: boolean;
}) {
  // Pick a complete-shape tone palette for the indicator. Each branch
  // returns concrete hex strings rather than a palette ref, because the
  // three palettes (ink/amber/slate) don't share identical index sets —
  // ink has 50/100/200/500/600/700, amber has 100/500/600/700, slate has
  // every step. Resolving to literals avoids missing-index TS errors.
  const tone = state.shouldInject
    ? {
        bg: colors.ink[50],
        border: colors.ink[200],
        icon: colors.ink[600],
        tagBg: colors.ink[100],
        tagBorder: colors.ink[200],
        tagText: colors.ink[700],
      }
    : state.resolvedSource === 'ModelDoesNotSupport'
    ? {
        bg: colors.amber[100],
        border: colors.amber[500],
        icon: colors.amber[600],
        tagBg: colors.amber[100],
        tagBorder: colors.amber[500],
        tagText: colors.amber[700],
      }
    : {
        bg: colors.slate[100],
        border: colors.slate[200],
        icon: colors.slate[600],
        tagBg: colors.slate[100],
        tagBorder: colors.slate[300],
        tagText: colors.slate[700],
      };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 8,
        background: isDark ? colors.slate[800] : tone.bg,
        border: `1px dashed ${isDark ? colors.slate[700] : tone.border}`,
      }}
    >
      <ThunderboltFilled style={{ color: tone.icon, fontSize: 16 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text style={{ fontSize: 12.5, color: isDark ? colors.slate[300] : colors.slate[600] }}>
            <strong>At runtime:</strong>
          </Text>
          {state.shouldInject ? (
            <Tag color="blue" style={{ fontFamily: "'JetBrains Mono', monospace", margin: 0 }}>
              {state.resolvedWireValue || state.resolvedLevelName?.toLowerCase()}
            </Tag>
          ) : (
            <Tag style={{ margin: 0, color: tone.tagText, borderColor: tone.tagBorder, background: tone.tagBg }}>
              not injected
            </Tag>
          )}
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            ({SOURCE_LABEL[state.resolvedSource] ?? state.resolvedSource})
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          {state.resolvedRationale}
        </Text>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// History drawer — audit trail
// ────────────────────────────────────────────────────────────────────────────

function HistoryDrawer({
  agentId,
  open,
  onClose,
}: {
  agentId: number;
  open: boolean;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<ReasoningHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getReasoningHistory(agentId);
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) void message.error('Load history failed: ' + (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId, open]);

  return (
    <Drawer
      title="Reasoning effort — change history"
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No changes yet — this agent is at the original setting."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r) => (
            <HistoryRow key={r.id} row={r} />
          ))}
        </div>
      )}
    </Drawer>
  );
}

function HistoryRow({ row }: { row: ReasoningHistoryRow }) {
  const when = new Date(row.changedAtUtc);
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${colors.slate[200]}`,
        borderRadius: 8,
        background: '#FFFFFF',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {row.oldLevelName ? (
          <>
            <Tag>{row.oldLevelName}</Tag>
            <Text type="secondary">→</Text>
            <Tag color="blue">{row.newLevelName}</Tag>
          </>
        ) : (
          <>
            <Tag color="blue">{row.newLevelName}</Tag>
            <Text type="secondary" style={{ fontSize: 11.5 }}>(initial set)</Text>
          </>
        )}
      </div>
      {row.reason && (
        <Paragraph style={{ margin: '4px 0', fontSize: 12.5, color: colors.slate[700] }}>
          <Text type="secondary">Reason: </Text>
          {row.reason}
        </Paragraph>
      )}
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        by user #{row.changedByUserId} · {when.toLocaleString()}
      </Text>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Downgrade prompt — Modal.confirm with a textarea for the reason
// Returns Promise<string | null> — null if cancelled.
// ────────────────────────────────────────────────────────────────────────────

function promptForReason(fromLevel: string, toLevel: string): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    let reasonValue = '';
    Modal.confirm({
      title: `Downgrade reasoning effort: ${fromLevel} → ${toLevel}?`,
      icon: <WarningFilled style={{ color: colors.amber[500] }} />,
      width: 520,
      content: (
        <div>
          <Paragraph style={{ marginTop: 8 }}>
            Lower reasoning effort means less careful answers. For agents that handle
            life-safety or clinical decisions, this can affect quality. Please record
            why you're making this change.
          </Paragraph>
          <Input.TextArea
            placeholder={'e.g. "Verified with clinical SME — this agent is education-only, does not handle triage."'}
            rows={3}
            onChange={(e) => {
              reasonValue = e.target.value;
            }}
            autoFocus
          />
        </div>
      ),
      okText: 'Downgrade',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => resolve(reasonValue.trim() || '(no reason given)'),
      onCancel: () => resolve(null),
    });
  });
}

export default ReasoningEffortCard;
